import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { FetchStatus, LanderCookie, System } from "../commons/Constants";
import { yahooXmlFetch, getYahooRSUri, sendYahooEvent } from "./yahooFeed";
import { useLanderConfig } from "../commons/ConfigContext";
import { getCookie } from "../commons/HelperFunctions";
import { YahooAds } from "./YahooAds";
import { YahooRS } from "./YahooRS";
import { SearchBox } from "./SearchBox";
import classes from "./YahooAds.module.scss";
import PropTypes from "prop-types";
import classNames from "classnames";

interface YahooAdContentProps {
  isAdClicked: boolean;
  xmlResult: Document | null;
}

const YahooAdContent: React.FC<YahooAdContentProps> = ({
  isAdClicked,
  xmlResult,
}) => {
  return (
    <>
      {isAdClicked ? (
        <YahooAds xmlResult={ xmlResult } />
      ) : (
        <YahooRS xmlResult={ xmlResult } />
      )}
      <SearchBox xmlResult={ xmlResult } />
    </>
  );
};

YahooAdContent.propTypes = {
  isAdClicked: PropTypes.bool,
  xmlResult: PropTypes.instanceOf(Document),
};

export const YahooContent: React.FC = () => {
  const { landerConfig, mergedConfig } = useLanderConfig();

  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);
  const [xmlResult, setXmlResult] = useState<Document | null>(null);

  const isAdClicked = new URLSearchParams(window.location.search).has("qs");

  useEffect(() => {
    // some bots don't have cookies, which results in their ip cookie having an unexpected value
    // in that case, getCookie will return an empty string
    if (!getCookie(LanderCookie.CLIENT_IP)) return;
    let ignore = false;

    let yahooUrl: string;
    // forward query params from browser url
    if (isAdClicked) yahooUrl = window.location.search;
    else
      yahooUrl = getYahooRSUri({
        rootDomain: landerConfig.domain.rootDomain,
        keywords: mergedConfig.keywords,
        system: landerConfig.system,
        customerId: landerConfig.customerId,
      });

    yahooXmlFetch(yahooUrl).then((v) => {
      if (ignore) return;
      setFetchState(v.fetchStatus);
      setXmlResult(v.xmlResult);
      if (!isAdClicked) {
        // logging ad clicks is now done right on the ad link
        sendYahooEvent(v.message, false, landerConfig);
      }
    });

    return () => {
      ignore = true;
    };
  }, [
    landerConfig,
    landerConfig.domain.rootDomain,
    mergedConfig.keywords,
    isAdClicked,
  ]);

  let yahooContent: React.ReactNode;
  if (fetchState === FetchStatus.PENDING) {
    yahooContent = null;
  } else if (fetchState === FetchStatus.SUCCESS) {
    yahooContent = (
      <YahooAdContent isAdClicked={ isAdClicked } xmlResult={ xmlResult } />
    );
  } else {
    yahooContent = (
      <div className={ classes.blockedContent }>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }

  return (
    <div
      className={ classNames(classes.yahooFeed, {
        [classes.pwYahoo]: landerConfig.system === System.PARKWEB,
      }) }
    >
      {yahooContent}
    </div>
  );
};
